const DEFAULTS = {
  headerSelector: 'header-component',
  filterSelector: '[data-template="collection"] .facets-toggle',
  mobileMedia: '(max-width: 749px)',
  scrollThreshold: 2,
  topRevealOffset: 8,
};

/** @type {{
 *  header: HTMLElement | null;
 *  filterBar: HTMLElement | null;
 *  breadcrumb: HTMLElement | null;
 *  lastScrollY: number;
 *  currentScrollY: number;
 *  ticking: boolean;
 *  rafId: number;
 *  rebindRafId: number;
 * }} */
const state = {
  header: null,
  filterBar: null,
  breadcrumb: null,
  lastScrollY: window.scrollY,
  currentScrollY: window.scrollY,
  ticking: false,
  rafId: 0,
  rebindRafId: 0,
};

const getConfig = () => {
  const body = document.body;
  return {
    headerSelector: body?.dataset.stickyHeaderSelector || DEFAULTS.headerSelector,
    filterSelector: body?.dataset.stickyFilterSelector || DEFAULTS.filterSelector,
    mobileMedia: DEFAULTS.mobileMedia,
    scrollThreshold: DEFAULTS.scrollThreshold,
    topRevealOffset: DEFAULTS.topRevealOffset,
  };
};

const isCollectionPage = () => {
  const template = document.body?.dataset.template || '';
  return template.startsWith('collection');
};

const isMobile = () => window.matchMedia(getConfig().mobileMedia).matches;

const setHeaderHeightVar = () => {
  if (!(state.header instanceof HTMLElement)) return;
  const headerSection = state.header.closest('.header-section');
  const headerHeight = headerSection instanceof HTMLElement ? headerSection.offsetHeight : state.header.offsetHeight;
  document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  document.body?.style.setProperty('--header-height', `${headerHeight}px`);
};

const setBreadcrumbHeightVar = () => {
  const breadcrumbHeight = state.breadcrumb instanceof HTMLElement ? state.breadcrumb.offsetHeight : 0;
  document.documentElement.style.setProperty('--collection-breadcrumb-height', `${breadcrumbHeight}px`);
  document.body?.style.setProperty('--collection-breadcrumb-height', `${breadcrumbHeight}px`);
};

/** @param {boolean} visible */
const setHeaderVisible = (visible) => {
  const body = document.body;
  if (!body) return;
  body.classList.toggle('header-visible', visible);
  body.classList.toggle('header-hidden', !visible);
};

const updateStickyUI = () => {
  state.ticking = false;

  if (!isCollectionPage() || !isMobile()) {
    setHeaderVisible(true);
    return;
  }

  const { scrollThreshold, topRevealOffset } = getConfig();
  const y = state.currentScrollY;
  const delta = y - state.lastScrollY;

  if (y <= topRevealOffset) {
    setHeaderVisible(true);
    state.lastScrollY = y;
    setHeaderHeightVar();
    setBreadcrumbHeightVar();
    return;
  }

  if (delta > scrollThreshold) {
    setHeaderVisible(false);
  } else if (delta < -scrollThreshold) {
    setHeaderVisible(true);
  }

  state.lastScrollY = y;
  setHeaderHeightVar();
  setBreadcrumbHeightVar();
};

const onScroll = () => {
  state.currentScrollY = window.scrollY;
  if (state.ticking) return;
  state.ticking = true;
  state.rafId = window.requestAnimationFrame(updateStickyUI);
};

const bindElements = () => {
  const { headerSelector, filterSelector } = getConfig();
  state.header = document.querySelector(headerSelector);
  state.filterBar = document.querySelector(filterSelector);
  state.breadcrumb = document.querySelector('.collection-sticky-breadcrumb');
  setHeaderHeightVar();
  setBreadcrumbHeightVar();
};

const scheduleRebind = () => {
  if (state.rebindRafId) return;

  let attempts = 0;
  const maxAttempts = 120;

  const step = () => {
    state.rebindRafId = 0;
    attempts += 1;
    bindElements();
    updateStickyUI();

    const headerReady = state.header instanceof HTMLElement;
    const filterReady = state.filterBar instanceof HTMLElement;

    if ((!headerReady || !filterReady) && attempts < maxAttempts) {
      state.rebindRafId = window.requestAnimationFrame(step);
    }
  };

  state.rebindRafId = window.requestAnimationFrame(step);
};

const init = () => {
  if (!isCollectionPage()) return;

  bindElements();
  updateStickyUI();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', scheduleRebind, { passive: true });
  window.addEventListener('orientationchange', scheduleRebind, { passive: true });

  document.addEventListener('shopify:section:load', scheduleRebind);
  document.addEventListener('filter:update', () => {
    // Facets re-render asynchronously; keep rebinding for a short window.
    scheduleRebind();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
